"use client";

import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Button, ButtonProps } from "@/components/atoms/Button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/molecules/Form";
import CalendarIcon from "@/icons/__generated__/Calendar/Calendar20";
import { cn } from "@/lib/utils";
import {
  getDisplayedValue,
  hasValue,
  normalizeDateValue,
} from "./date-picker-utils";
import { DatePicker, DatePickerProps } from "./DatePicker";

/** Formats a selected value for the field's trigger (e.g. a compact list of dates). */
export type DatePickerFieldFormatValue = (
  value: NonNullable<DatePickerProps["value"]>,
) => React.ReactNode;

const DatePickerFieldTrigger = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    errorMsg?: string;
    readOnly?: boolean;
    value?: Date;
    placeholder?: React.ReactNode;
    dateFormat?: string;
    displayCalendarIcon?: boolean;
    formatValue?: DatePickerFieldFormatValue;
  }
>(
  (
    {
      errorMsg,
      fullWidth,
      readOnly,
      disabled,
      value,
      placeholder,
      dateFormat,
      displayCalendarIcon = true,
      formatValue,
      ...buttonProps
    },
    ref,
  ) => {
    const dateValue = normalizeDateValue(value);
    const displayedValue =
      formatValue && hasValue(dateValue)
        ? formatValue(dateValue)
        : getDisplayedValue(dateValue, placeholder, dateFormat);

    return (
      <Button
        variant="outline-1"
        appearance="white"
        prefix={
          displayCalendarIcon ? (
            <CalendarIcon className="tw-text-gray-900" />
          ) : null
        }
        fullWidth={fullWidth}
        disabled={disabled}
        truncate
        ref={ref as React.RefObject<HTMLButtonElement>}
        {...buttonProps}
        className={cn(
          "tw-justify-start tw-text-left tw-body-2 tw-max-w-full hover:tw-bg-base-white",
          buttonProps?.className,
          {
            "tw-pointer-events-none": readOnly,
            "tw-text-gray-600": !hasValue(dateValue),
            "tw-border-red-800": !!errorMsg,
          },
        )}
      >
        <span
          className="tw-truncate"
          data-testid={buttonProps.testId && `${buttonProps.testId}-value`}
        >
          {displayedValue}
        </span>
      </Button>
    );
  },
);
DatePickerFieldTrigger.displayName = "DatePickerFieldTrigger";

export type DatePickerFieldProps = DatePickerProps & {
  name: string;
  label?: React.ReactNode;
  subcopy?: React.ReactNode;
  placeholder?: React.ReactNode;
  note?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  /** Formats the selected value shown in the field; defaults to the built-in date format. */
  formatValue?: DatePickerFieldFormatValue;
};

export const DatePickerField = ({
  name = "date",
  calendarProps,
  fullWidth,
  label,
  subcopy,
  placeholder,
  note,
  onValueChange,
  className,
  disabled,
  readOnly,
  triggerProps,
  popoverProps,
  testId,
  required,
  dateFormat,
  displayCalendarIcon,
  formatValue,
  contentClassName,
}: DatePickerFieldProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            disabled={disabled}
            readOnly={readOnly}
            className={cn({ "tw-w-full tw-flex-1": fullWidth }, className)}
          >
            {!!label && (
              <FormLabel subcopy={subcopy} required={required}>
                {label}
              </FormLabel>
            )}
            <DatePicker
              testId={testId}
              dateFormat={dateFormat}
              popoverProps={popoverProps}
              displayCalendarIcon={displayCalendarIcon}
              contentClassName={contentClassName}
              calendarProps={Object.assign(
                {
                  selected: normalizeDateValue(field.value),
                  onSelect: onValueChange || field.onChange,
                },
                calendarProps || {},
              )}
              trigger={
                <FormControl forwardErrorMsg>
                  <DatePickerFieldTrigger
                    dateFormat={dateFormat}
                    fullWidth={fullWidth}
                    readOnly={readOnly}
                    disabled={disabled}
                    value={field.value}
                    placeholder={placeholder}
                    ref={field.ref}
                    testId={testId ? `${testId}-trigger` : undefined}
                    {...(triggerProps || {})}
                    displayCalendarIcon={displayCalendarIcon}
                    formatValue={formatValue}
                  />
                </FormControl>
              }
            />
            <FormMessage>{note}</FormMessage>
          </FormItem>
        );
      }}
    />
  );
};
